import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Card from '../../atoms/Card';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import LabelledColumn from '../../atoms/LabelledColumn';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import StatusBadge from '../../atoms/StatusBadge/StatusBadge';
import { LogoIcon, WarningIcon } from '../../atoms/Icons';
import useBinControllerSensorStatus from '../../utils/hooks/useBinControllerSensorStatus';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';

import './BinControllerStatusCard.scss';

const LAN_STATUS_LABELS = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  unknown: 'Unknown',
};

const LAN_STATUS_BADGE = {
  connected: 'success',
  disconnected: 'error',
  unknown: 'warning',
};

function formatRelativeTime(timestamp) {
  if (timestamp == null) {
    return '—';
  }
  return dayjs.tz(Number(timestamp) * 1000).fromNow();
}

function deriveLanHealth(summary, isLoading) {
  if (!summary) {
    return isLoading ? 'warning' : 'warning';
  }
  if (summary.disconnectedCount > 0) {
    return 'error';
  }
  if (summary.unknownCount > 0) {
    return 'warning';
  }
  return 'success';
}

function buildLanSummaryText(summary) {
  if (!summary) {
    return 'Loading…';
  }

  const parts = [];
  if (summary.connectedCount > 0) {
    parts.push(`${summary.connectedCount} connected`);
  }
  if (summary.disconnectedCount > 0) {
    parts.push(`${summary.disconnectedCount} disconnected`);
  }
  if (summary.unknownCount > 0) {
    parts.push(`${summary.unknownCount} unknown`);
  }

  return parts.length > 0 ? parts.join(', ') : 'No sensors configured';
}

function buildTooltipContent(devices) {
  if (!devices?.length) {
    return 'No feed line sensors configured for this bin controller.';
  }

  return (
    <div className="BinControllerStatusCard-tooltipContent">
      {devices.map((device) => (
        <div key={device.serial} className="BinControllerStatusCard-tooltipRow">
          <span className="BinControllerStatusCard-tooltipSerial">{device.serial}</span>
          <span className="BinControllerStatusCard-tooltipFeedLine">{device.feedLineName || 'Unknown'}</span>
          <StatusBadge
            status={LAN_STATUS_BADGE[device.lanStatus] ?? 'warning'}
            text={LAN_STATUS_LABELS[device.lanStatus] ?? device.lanStatus}
          />
          <span className="BinControllerStatusCard-tooltipTime">{formatRelativeTime(device.lastEvent?.startedAt)}</span>
        </div>
      ))}
    </div>
  );
}

function BinControllerStatusCard({
  deviceId,
  binNames = [],
  isOnline = false,
  loading: parentLoading = false,
  className = '',
}) {
  const { loading: hookLoading, error, devices, summary, controller } = useBinControllerSensorStatus({
    deviceId,
    skip: parentLoading || !deviceId,
    pollInterval: 0,
  });

  const isLoading = parentLoading || (hookLoading && !summary);
  const serial = controller?.serial ?? '—';
  const lanHealth = deriveLanHealth(summary, isLoading);
  const binLabel = binNames.filter(Boolean).join(', ') || '—';
  const IdWrappedTitle = withStaffOnlyIdWrapper(serial);

  if (isLoading) {
    return (
      <Card
        loading
        className={`BinControllerStatusCard BinControllerStatusCard--loading ${className}`}
        contentClassName="BinControllerStatusCard-cardContentWrapper"
        status="warning"
        accentLocation="top"
      >
        <LoadingSkeleton className="BinControllerStatusCard-typeLabel BinControllerStatusCard-typeLabel--loading" />
        <LoadingSkeleton className="BinControllerStatusCard-serial BinControllerStatusCard-serial--loading" />
        <div className="BinControllerStatusCard-content">
          <div className="BinControllerStatusCard-statusWrapper">
            <LoadingSkeleton className="BinControllerStatusCard-onlineStatus BinControllerStatusCard-onlineStatus--loading" />
          </div>
          <div className="BinControllerStatusCard-details">
            <LabelledColumn loading className="BinControllerStatusCard-bins--loading" heading="Bins" />
            <LabelledColumn loading className="BinControllerStatusCard-lanBadge--loading" heading="Flo Sensors" />
          </div>
        </div>
      </Card>
    );
  }

  const tooltipDescription = error
    ? 'Failed to load connected sensor status.'
    : buildTooltipContent(devices);

  return (
    <Card
      className={`BinControllerStatusCard ${className}`}
      contentClassName="BinControllerStatusCard-cardContentWrapper"
      status={isOnline ? 'success' : 'error'}
      accentLocation="top"
    >
      <div className="BinControllerStatusCard-titleRow">
        <span className="BinControllerStatusCard-typeLabel">Bin Operator</span>
        <IdWrappedTitle data={deviceId} hoverText="Copy Device ID" className="BinControllerStatusCard-serial" />
      </div>
      <div className="BinControllerStatusCard-content">
        <div className="BinControllerStatusCard-statusWrapper">
          {isOnline
            ? <LogoIcon className="BinControllerStatusCard-icon BinControllerStatusCard-icon--success" />
            : <WarningIcon className="BinControllerStatusCard-icon BinControllerStatusCard-icon--error" />
          }
          <div className={`BinControllerStatusCard-onlineStatus BinControllerStatusCard-onlineStatus--${isOnline ? 'success' : 'error'}`}>
            {isOnline ? 'Online' : 'Offline'}
          </div>
        </div>
        <div className="BinControllerStatusCard-details">
          <LabelledColumn heading="Bins">{binLabel}</LabelledColumn>
          <LabelledColumn heading="Flo Sensors">
            <FeedFloToolTip description={tooltipDescription} position="top" multiline textClassName="BinControllerStatusCard-tooltipBox">
              <div className="BinControllerStatusCard-lanWrapper">
                <StatusBadge
                  status={lanHealth}
                  text={error ? 'Unavailable' : buildLanSummaryText(summary)}
                />
              </div>
            </FeedFloToolTip>
          </LabelledColumn>
        </div>
      </div>
    </Card>
  );
}

BinControllerStatusCard.propTypes = {
  deviceId: PropTypes.string.isRequired,
  binNames: PropTypes.arrayOf(PropTypes.string),
  isOnline: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default BinControllerStatusCard;
